
import { TrendingUp, Building2, Users2, Globe } from "lucide-react";
import { Card } from "@/components/ui/card";
import { BenchmarkComparison } from "../charts/BenchmarkComparison";
import { OrganizationDetails } from "@/types/assessment";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface BenchmarkSectionProps {
  dimensionScores: Record<string, number>;
  industry: string;
  organizationDetails: OrganizationDetails;
}

export const BenchmarkSection = ({ 
  dimensionScores, 
  industry,
  organizationDetails 
}: BenchmarkSectionProps) => {
  // Format organization context for display
  const contextData = [
    {
      icon: <Building2 className="w-4 h-4" />,
      label: "Organization Type",
      value: organizationDetails.organizationType || "Not specified"
    },
    {
      icon: <Users2 className="w-4 h-4" />,
      label: "Organization Size",
      value: organizationDetails.size
    },
    {
      icon: <Globe className="w-4 h-4" />,
      label: "Country",
      value: organizationDetails.country || "Not specified"
    }
  ];

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="w-6 h-6 text-primary" />
        <h2 className="text-2xl font-bold text-primary">Industry Benchmark Analysis</h2>
      </div>

      {/* Organization Context */}
      <div className="mb-6 bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Benchmark Context</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]"></TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contextData.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="text-primary">{item.icon}</TableCell>
                <TableCell className="font-medium">{item.label}</TableCell>
                <TableCell>{item.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-6">
        <p className="text-muted-foreground">
          This analysis compares your organization's AI maturity scores against industry benchmarks, 
          highlighting areas where you lead or lag compared to peers in the {industry} sector 
          with similar characteristics.
        </p>
        <BenchmarkComparison 
          dimensionScores={dimensionScores} 
          organizationType={organizationDetails.organizationType}
          size={organizationDetails.size}
        />
      </div>
    </Card>
  );
};
